import React, { useEffect, useState } from "react";
import { BriefcaseBusiness, Loader2 } from "lucide-react";
import axios from "axios";
import { Link } from "react-router-dom";

function CaseStudies() {
  const [caseStudies, setCaseStudies] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);

  const caseStudiesPerPage = 8;

  const lastIndex = currentPage * caseStudiesPerPage;
  const firstIndex = lastIndex - caseStudiesPerPage;

  const currentCaseStudies = caseStudies.slice(firstIndex, lastIndex);
  const totalPage = Math.ceil(caseStudies.length / caseStudiesPerPage);

  const fetchData = async () => {
    try {
      setLoading(true);

      const res = await axios.get(
        "https://goawin.xyz/api/domain/case-studies",
        { withCredentials: true },
      );

      setCaseStudies(res.data.data || res.data.caseStudies || []);
    } catch (err) {
      console.log("Server Error");
      setCaseStudies([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const imageUrl = (image) => {
    if (!image) return "/placeholder.jpg";
    if (image.startsWith("http")) return image;
    return `https://goawin.xyz/${image}`;
  };

  if (loading) {
    return (
      <section className="relative flex min-h-[500px] items-center justify-center overflow-hidden bg-[#fafafa] py-20 dark:bg-black">
        <div className="absolute left-0 top-0 h-[500px] w-[500px] rounded-full bg-primary/10 blur-[140px]" />
        <div className="absolute bottom-0 right-0 h-[500px] w-[500px] rounded-full bg-orange-300/10 blur-[140px]" />

        <div className="relative z-10 flex flex-col items-center gap-4">
          <Loader2 className="h-10 w-10 animate-spin text-primary" />
          <p className="text-sm font-semibold text-gray-600 dark:text-gray-300">
            Loading case studies...
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="relative overflow-hidden bg-[#fafafa] py-20 dark:bg-black">
      <div className="absolute left-0 top-0 h-[500px] w-[500px] rounded-full bg-primary/10 blur-[140px]" />
      <div className="absolute bottom-0 right-0 h-[500px] w-[500px] rounded-full bg-orange-300/10 blur-[140px]" />

      <div className="absolute inset-0 bg-[linear-gradient(rgba(0,0,0,0.05)_1px,transparent_1px),linear-gradient(90deg,rgba(0,0,0,0.05)_1px,transparent_1px)] bg-[size:70px_70px] dark:bg-[linear-gradient(rgba(255,255,255,0.05)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.05)_1px,transparent_1px)]" />

      <div className="relative mx-auto max-w-7xl px-5">
        <div className="mx-auto mb-14 max-w-3xl text-center">
          <h2 className="mt-5 text-4xl font-black tracking-tight text-gray-950 dark:text-white md:text-5xl">
            Our Recent Projects
          </h2>

          <p className="mx-auto mt-4 max-w-2xl text-sm leading-7 text-gray-600 dark:text-gray-300 md:text-base">
            Explore our latest web development projects, CMS solutions,
            ecommerce platforms, and custom web applications.
          </p>
        </div>

        {caseStudies.length > 0 ? (
          <>
            <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
              {currentCaseStudies.map((caseStudy) => (
                <article
                  key={caseStudy._id}
                  className="group overflow-hidden rounded-lg border border-gray-200/80 bg-white/90 shadow-sm backdrop-blur-xl transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl dark:border-white/10 dark:bg-white/[0.04]"
                >
                  <Link
                    to={`/case-study/${caseStudy.slug}`}
                    className="group relative block overflow-hidden rounded-lg"
                  >
                    <img
                      src={imageUrl(caseStudy.featuredImage)}
                      alt={`${caseStudy.title} case study`}
                      className="w-full object-cover transition duration-700 group-hover:scale-105"
                    />

                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />

                    <div className="absolute left-5 top-5 flex items-center gap-2 rounded-full bg-white/90 px-3 py-1.5 text-xs font-bold text-gray-950 backdrop-blur-md">
                      <BriefcaseBusiness size={14} className="text-primary" />
                      {caseStudy.industry || "Industry"}
                    </div>

                    <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
                      <h3 className="mt-2 text-2xl font-black text-white">
                        {caseStudy.title}
                      </h3>

                      <p className="mt-2 line-clamp-2 text-sm text-gray-200">
                        {caseStudy.shortDescription || caseStudy.description}
                      </p>
                    </div>
                  </Link>
                </article>
              ))}
            </div>

            {totalPage > 1 && (
              <div className="pt-10">
                <div className="flex justify-end gap-2">
                  <button
                    disabled={currentPage === 1}
                    className="rounded border border-primary px-3 py-1 text-sm text-primary disabled:cursor-not-allowed disabled:opacity-40"
                    onClick={() =>
                      setCurrentPage((prev) => Math.max(prev - 1, 1))
                    }
                  >
                    Prev
                  </button>

                  <button className="rounded bg-primary px-3 py-1 text-sm text-white">
                    {currentPage} / {totalPage}
                  </button>

                  <button
                    disabled={currentPage === totalPage}
                    className="rounded border border-primary px-3 py-1 text-sm text-primary disabled:cursor-not-allowed disabled:opacity-40"
                    onClick={() =>
                      setCurrentPage((prev) => Math.min(prev + 1, totalPage))
                    }
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="mt-10 rounded-3xl border border-gray-200 bg-white p-10 text-center text-gray-500 dark:border-white/10 dark:bg-white/[0.04] dark:text-gray-300">
            No case studies found.
          </div>
        )}
      </div>
    </section>
  );
}

export default CaseStudies;
